/**
 * Centralized logic for activity selection, especially for the main "Commencer" button
 */

import { getUrlParameter } from './urlUtils';

// Global cache for activity progress data to avoid repeated Firebase calls
let cachedActivityProgress = null;
let cachedUserId = null;
let cachedActivityProgressIsComplete = false;
// Which learning plan the cached progress was recorded against — see
// syncActivityProgressCacheToPlan below.
let cachedPlanKey = null;
export const ACTIVITY_PROGRESS_UPDATED_EVENT = 'madameai:activity-progress-updated';

/**
 * Drops the cached activity progress, including the plan it belonged to.
 */
export const clearActivityProgressCache = () => {
  cachedActivityProgress = null;
  cachedUserId = null;
  cachedActivityProgressIsComplete = false;
  cachedPlanKey = null;
};

export const clampProgressIndex = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.max(0, Math.min(100, Math.round(number)));
};

// The server owns completion (any attempt >= 95, sticky) and reports it via
// status — never re-derive completed from a progress threshold here. The
// >= 100 check only covers legacy records written before statuses existed.
export const normalizeActivityProgressStatus = (status, progressIndex) => {
  if (status === 'completed' || progressIndex >= 100) return 'completed';
  if (progressIndex > 0) return 'in_progress';
  return status === 'started' || status === 'in_progress' ? 'in_progress' : 'not_started';
};

export const normalizeActivityProgressEntry = (progress = {}) => {
  const progressIndex = clampProgressIndex(progress.progressIndex ?? progress.progress?.progressIndex ?? 0);
  return {
    ...progress,
    status: normalizeActivityProgressStatus(progress.status || progress.progress?.status || 'not_started', progressIndex),
    progressIndex,
  };
};

/**
 * Updates the cached activity progress data
 * This should be called whenever progress data is fetched elsewhere in the app
 * @param {string} userId - The user ID
 * @param {Object} progressData - The progress data object
 */
export const updateActivityProgressCache = (userId, progressData, options = {}) => {
  cachedUserId = userId;
  cachedActivityProgressIsComplete = !!progressData && options.complete !== false;
  cachedActivityProgress = progressData
    ? Object.fromEntries(
        Object.entries(progressData).map(([activityId, progress]) => [
          String(activityId),
          normalizeActivityProgressEntry(progress),
        ])
      )
    : progressData;
};

export const mergeActivityProgressCache = (userId, activityId, progress) => {
  if (!userId || activityId === undefined || activityId === null || !progress) {
    return null;
  }

  const normalizedActivityId = String(activityId);
  if (cachedUserId !== userId || !cachedActivityProgress) {
    cachedUserId = userId;
    cachedActivityProgress = {};
    cachedActivityProgressIsComplete = false;
  }

  const existingProgress = cachedActivityProgress[normalizedActivityId] || {};
  const mergedProgress = normalizeActivityProgressEntry({
    ...existingProgress,
    ...progress,
  });

  cachedActivityProgress = {
    ...cachedActivityProgress,
    [normalizedActivityId]: mergedProgress,
  };

  return mergedProgress;
};

export const notifyActivityProgressUpdate = ({ userId, activityId, progress }) => {
  const mergedProgress = mergeActivityProgressCache(userId, activityId, progress);
  if (!mergedProgress) {
    return null;
  }

  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(ACTIVITY_PROGRESS_UPDATED_EVENT, {
      detail: {
        userId,
        activityId: String(activityId),
        progress: mergedProgress,
      },
    }));
  }

  return mergedProgress;
};

export const subscribeActivityProgressUpdates = (listener) => {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return () => {};
  }

  const handleProgressUpdate = (event) => {
    listener(event.detail || {});
  };

  window.addEventListener(ACTIVITY_PROGRESS_UPDATED_EVENT, handleProgressUpdate);
  return () => window.removeEventListener(ACTIVITY_PROGRESS_UPDATED_EVENT, handleProgressUpdate);
};

/**
 * Gets the cached activity progress data
 * @param {string} userId - The user ID to check against cache
 * @returns {Object|null} Cached progress data or null if not available/stale
 */
export const getCachedActivityProgress = (userId, options = {}) => {
  if (
    cachedUserId === userId
    && cachedActivityProgress
    && (!options.requireComplete || cachedActivityProgressIsComplete)
  ) {
    return cachedActivityProgress;
  }
  return null;
};

/**
 * Determines what activity to use for the main "Commencer" button
 * Priority order:
 * 1. Partner URL activity (?activity=) - always respected for partner integration
 * 2. Tutorial activity (from welcome modal)
 * 3. Next learning-path activity from planner
 * 4. null (triggers learning goal prompt or free-form conversation)
 *
 * @param {string} currentSelectedActivity - The currently selected activity from sharedState (optional)
 * @param {Object} plannerData - The current planner data from PlannerContext
 * @returns {string} The activity value to use (e.g., "learning-path-3" or "learning-path")
 */
export const getMainStartActivity = (currentSelectedActivity, plannerData) => {

  // Partner integration: URL activity parameter takes highest priority
  // This ensures partner-provided activities are always used, even for users with planner data
  const urlActivity = getUrlParameter('activity');
  if (urlActivity) {
    console.log(`🤝 Using partner URL activity: ${urlActivity}`);
    return urlActivity;
  }

  // If tutorial is selected, use it (from welcome modal "Start Tutorial" button)
  if (currentSelectedActivity === 'tutorial') {
    return 'tutorial';
  }

  // Try to get the next non-started learning path activity
  const nextActivity = getNextLearningPathActivity(plannerData);
  if (nextActivity) {
    return nextActivity;
  }

  // No planner data available - return null to trigger learning goal prompt
  // If user declines to create a goal, they'll start a free-form conversation (null activity)
  return null;

};

/**
 * Determines what activity to use for the "Start conversation" button (free-form chat)
 * This should always return null for free-form conversation
 * 
 * @returns {null} Always returns null for free-form conversation
 */
export const getFreeformStartActivity = () => {
  return null;
};

/**
 * Gets the planner's activities (numeric keys only) sorted by activityId,
 * leaving out metadata fields like goal, number_activities and createdAt.
 *
 * @param {Object} plannerData - The current planner data from PlannerContext
 * @returns {Array<Object>} Sorted activity objects (empty when there are none)
 */
const getSortedPlannerActivities = (plannerData) => (
  Object.entries(plannerData || {})
    .filter(([key, value]) => !isNaN(key) && typeof value === 'object' && value !== null)
    .map(([, activity]) => activity)
    .sort((a, b) => parseInt(a.activityId) - parseInt(b.activityId))
);

const isActivityIncomplete = (activity, progressCache) => {
  const progress = progressCache?.[activity.activityId] || activity.progress;
  if (!progress) return true;
  return normalizeActivityProgressStatus(progress.status, clampProgressIndex(progress.progressIndex)) !== 'completed';
};

/**
 * Gets the first incomplete learning path activity in order, or a random one
 * once every activity is completed. Progress comes from the cache when
 * available, otherwise from the planner's embedded progress.
 *
 * @param {Object} plannerData - The current planner data from PlannerContext
 * @returns {string|null} The next activity value or null if no planner data
 */
export const getNextLearningPathActivity = (plannerData) => {
  if (!plannerData || plannerData.number_activities === 0) {
    return null;
  }

  const activities = getSortedPlannerActivities(plannerData);

  if (activities.length === 0) {
    return null;
  }

  const progressCache = getCachedActivityProgress(cachedUserId);
  const incompleteActivity = activities.find(activity => isActivityIncomplete(activity, progressCache));
  if (incompleteActivity) {
    return `learning-path-${incompleteActivity.activityId}`;
  }

  // Every activity is completed — offer a random one to redo
  const selectedActivity = activities[Math.floor(Math.random() * activities.length)];
  return `learning-path-${selectedActivity.activityId}`;
};

/**
 * Whether the user finished every activity in their learning plan. Drives the
 * plan-complete state, so it stays strict: a plan whose activities aren't all
 * present yet (still generating, or truncated) is not complete, otherwise the
 * celebration would contradict the "18/20" the progress bar shows.
 *
 * @param {Object} plannerData - The current planner data from PlannerContext
 * @returns {boolean} True when every planned activity is completed
 */
export const isLearningPlanComplete = (plannerData) => {
  if (!plannerData || !(plannerData.number_activities > 0)) {
    return false;
  }

  const activities = getSortedPlannerActivities(plannerData);
  if (activities.length < plannerData.number_activities) {
    return false;
  }

  const progressCache = getCachedActivityProgress(cachedUserId);
  return activities.every(activity => !isActivityIncomplete(activity, progressCache));
};

/**
 * Identifies which plan the cached progress belongs to. Positions alone can't
 * do it — every plan has an activity "1" — so the goal and the activity titles
 * are what make a plan recognisable.
 */
const getPlanIdentityKey = (plannerData) => {
  if (!plannerData || !(plannerData.number_activities > 0)) {
    return null;
  }

  const activities = getSortedPlannerActivities(plannerData);
  if (activities.length === 0) {
    return null;
  }

  return [
    plannerData.goal || '',
    ...activities.map(activity => `${activity.activityId}:${activity.title || ''}`),
  ].join('|');
};

/**
 * Points the progress cache at the plan currently in play, dropping it when
 * that plan changes. The cache is keyed by activity position, which only means
 * something within one plan: activity 1 of a new plan is a different lesson
 * from activity 1 of the plan it replaced. Without this, a learner who
 * finishes a plan and immediately sets a new goal carries their completions
 * over and the fresh plan looks finished the moment it appears.
 *
 * @param {Object} plannerData - The current planner data from PlannerContext
 * @returns {boolean} True when a previous plan's cached progress was dropped
 */
export const syncActivityProgressCacheToPlan = (plannerData) => {
  const planKey = getPlanIdentityKey(plannerData);
  if (!planKey || planKey === cachedPlanKey) {
    return false;
  }

  const replacedAnotherPlan = cachedPlanKey !== null;
  if (replacedAnotherPlan) {
    clearActivityProgressCache();
  }
  cachedPlanKey = planKey;
  return replacedAnotherPlan;
};

